import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLocale } from "@/contexts/LanguageContext";
import { Calendar, MessageCircle, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import consultationImg from "@/assets/consultation-popup.jpg";

const DISMISS_KEY = "martinez_popup_dismissed";
const DISMISS_HOURS = 24;
const SHOW_DELAY_MS = 60000; // 60 seconds

export function ConsultationPopup() {
  const [open, setOpen] = useState(false);
  const { locale, getLocalizedHref } = useLocale();

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < DISMISS_HOURS * 60 * 60 * 1000) return;
    }

    const timer = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setOpen(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  const isEs = locale === "es";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg p-0 border-primary/30 bg-card overflow-hidden gap-0">
        <DialogTitle className="sr-only">
          {isEs ? "Agende Su Consulta" : "Schedule Your Consultation"}
        </DialogTitle>

        {/* Image */}
        <div className="relative h-48 overflow-hidden">
          <img
            src={consultationImg}
            alt="Attorney consultation"
            className="w-full h-full object-cover"
            width={800}
            height={600}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 p-2 rounded-full bg-background/60 backdrop-blur-sm text-foreground hover:bg-background/80 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-8 pb-8 -mt-8 relative z-10">
          <p className="micro-label text-primary mb-2">
            {isEs ? "Martinez Law LLC" : "Martinez Law LLC"}
          </p>
          <h3 className="font-serif text-2xl font-bold text-foreground mb-3 tracking-[-0.02em]">
            {isEs ? "Su Estrategia Legal Comienza Aquí" : "Your Legal Strategy Starts Here"}
          </h3>
          <p className="text-muted-foreground mb-6 leading-relaxed">
            {isEs
              ? "Programe una consulta confidencial con nuestro equipo. Atención bilingüe disponible."
              : "Schedule a confidential consultation with our team. Bilingual support available on every call."}
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to={getLocalizedHref("/contact")}
              onClick={handleClose}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-ivory-cta text-ivory-cta-foreground px-6 py-3.5 rounded-md font-bold hover:bg-primary hover:text-primary-foreground transition-colors pulse-gold text-sm"
            >
              <Calendar className="h-4 w-4" />
              {isEs ? "Agendar Consulta" : "Book Consultation"}
            </Link>
            <a
              href="https://wa.me/17738183020"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClose}
              className="flex-1 inline-flex items-center justify-center gap-2 border border-[#128C7E]/60 text-[#128C7E] px-6 py-3.5 rounded-md font-bold hover:bg-[#128C7E]/10 transition-colors text-sm"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
          </div>

          <p className="text-xs text-muted-foreground/50 mt-4 text-center">
            {isEs
              ? "Se habla español · (773) 818-3020"
              : "Se habla español · (773) 818-3020"}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
